import React, { useState, useEffect, useContext, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import { useSnackbar } from "notistack"
import i18n from "i18n"

import AuthContext from "context/auth/authContext"
import Header from "./PurchaseOrder/Header"
import PurchaseOrderInfo from "./PurchaseOrder/PurchaseOrderInfo"
import PurchaseOrderConcepts from "./PurchaseOrder/PurchaseOrderConcepts"
import InvoiceNotes from "components/Invoices/Invoice/InvoiceNotes"
import InvoicePayment from "components/Invoices/Invoice/IvoicePayment"
import { formatDate } from "components/Invoices/utils/invoiceUtils"
import { adaptPurchaseOrderForBackend } from "./utils/purchaseOrderUtils"
import {
  useSinglePurchaseOrder,
  useCreatePurchaseOrder,
  useEditPurchaseOrder,
  useUpdatePurchaseOrderPaymentStatus,
  useConvertPurchaseOrderToExpense,
} from "./hooks/usePurchaseOrders"
import { useSeries } from "components/ProfileRefinament/series/hooks/useSeries"
import NoActiveSerieAlert from "components/ProfileRefinament/series/NoActiveSerieAlert"

const initialPurchaseOrderState = {
  projectId: null,
  purchaseOrderNumber: "",
  contact: "",
  issueDate: "",
  dueDate: "",
  notes: "",
  iban: "",
  paymentMethod: "transfer",
  paymentStatus: "unpaid",
  discount: 0,
  subtotal: 0,
  tax: 0,
  retention: 0,
  total: 0,
}

const PurchaseOrder = () => {
  const { purchaseOrderId, projectId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useContext(AuthContext)
  const organizationId = user ? user["custom:organizationId"] : undefined

  const isNew = purchaseOrderId === "new"
  const [purchaseOrderData, setPurchaseOrderData] = useState({
    ...initialPurchaseOrderState,
    projectId: isNew && projectId ? projectId : null,
  })
  const [selectedSerieSK, setSelectedSerieSK] = useState(null)
  const [hasValidationErrors, setHasValidationErrors] = useState(false)
  const [invalidContact, setInvalidContact] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [convertToExpenseDialogOpen, setConvertToExpenseDialogOpen] =
    useState(false)

  const getPurchaseOrder = useSinglePurchaseOrder(
    organizationId,
    purchaseOrderId,
    !isNew && !!organizationId && !!purchaseOrderId,
  )

  const purchaseOrderSeries = useSeries(organizationId, "purchase_orders", {
    enabled: isNew && !!organizationId,
  })
  const activePurchaseOrderSeries = (purchaseOrderSeries.data || []).filter(
    s => !s.archived,
  )

  const createPurchaseOrder = useCreatePurchaseOrder()
  const editPurchaseOrder = useEditPurchaseOrder()
  const updatePurchaseOrderPaymentStatus = useUpdatePurchaseOrderPaymentStatus()
  const convertPurchaseOrderToExpense = useConvertPurchaseOrderToExpense()

  useEffect(() => {
    if (!isNew && getPurchaseOrder.data) {
      setPurchaseOrderData(prev => {
        const raw = getPurchaseOrder.data
        const { projectId: rawProjectId, ...restRaw } = raw
        let contactValue = raw.contact
        if (contactValue && contactValue.SK) {
          contactValue = contactValue.SK
        }
        return {
          ...prev,
          ...restRaw,
          projectId: rawProjectId || null,
          contact: contactValue,
          issueDate: formatDate(raw.issueDate),
          dueDate: formatDate(raw.dueDate),
        }
      })
    }
  }, [isNew, getPurchaseOrder.data])

  const hasLinkedExpense =
    purchaseOrderData.expenseId != null &&
    String(purchaseOrderData.expenseId).trim() !== ""

  const handleBack = () => {
    if (projectId) {
      navigate(`/purchase-orders/${projectId}`)
    } else {
      navigate("/purchase-orders")
    }
  }

  const handleConvertToExpenseClick = useCallback(() => {
    const n = Array.isArray(purchaseOrderData.purchaseOrderConcepts)
      ? purchaseOrderData.purchaseOrderConcepts.length
      : 0
    if (isNew || n !== 1 || hasLinkedExpense) return
    setConvertToExpenseDialogOpen(true)
  }, [isNew, purchaseOrderData.purchaseOrderConcepts, hasLinkedExpense])

  const getPurchaseOrderSaveBlocker = useCallback(() => {
    const isEmpty = v => !v || (typeof v === "string" && v.trim() === "")
    if (invalidContact) {
      return { i18nKey: "invoices.selectClient", setHasValidationErrors: true }
    }
    if (isNew && activePurchaseOrderSeries.length === 0) {
      return {
        i18nKey: "purchaseOrders.noActiveSerie",
        setHasValidationErrors: true,
      }
    }
    if (isNew && activePurchaseOrderSeries.length > 1 && !selectedSerieSK) {
      return {
        i18nKey: "purchaseOrders.selectSerie",
        setHasValidationErrors: true,
      }
    }
    const conceptsMissingDescription = (
      purchaseOrderData.purchaseOrderConcepts || []
    ).some(c => isEmpty(c.description))
    if (
      isEmpty(purchaseOrderData.contact) ||
      isEmpty(purchaseOrderData.issueDate) ||
      isEmpty(purchaseOrderData.paymentMethod) ||
      conceptsMissingDescription
    ) {
      return { i18nKey: "invoices.missingFields", setHasValidationErrors: true }
    }
    return null
  }, [
    invalidContact,
    isNew,
    activePurchaseOrderSeries.length,
    selectedSerieSK,
    purchaseOrderData.contact,
    purchaseOrderData.issueDate,
    purchaseOrderData.paymentMethod,
    purchaseOrderData.purchaseOrderConcepts,
  ])

  const handleCloseConvertToExpenseDialog = useCallback(() => {
    if (convertPurchaseOrderToExpense.isLoading || isSaving) {
      return
    }
    setConvertToExpenseDialogOpen(false)
  }, [convertPurchaseOrderToExpense.isLoading, isSaving])

  const handleConfirmConvertToExpense = useCallback(async () => {
    if (hasLinkedExpense) {
      setConvertToExpenseDialogOpen(false)
      return
    }
    const n = Array.isArray(purchaseOrderData.purchaseOrderConcepts)
      ? purchaseOrderData.purchaseOrderConcepts.length
      : 0
    if (n !== 1) {
      setConvertToExpenseDialogOpen(false)
      return
    }
    if (!organizationId || !purchaseOrderId || purchaseOrderId === "new") {
      return
    }

    const blocker = getPurchaseOrderSaveBlocker()
    if (blocker) {
      if (blocker.setHasValidationErrors) setHasValidationErrors(true)
      enqueueSnackbar(i18n.t(blocker.i18nKey), { variant: "error" })
      return
    }
    setHasValidationErrors(false)

    const adapted = adaptPurchaseOrderForBackend(purchaseOrderData, {
      isCreate: false,
      purchaseOrderId,
    })

    setIsSaving(true)
    try {
      await editPurchaseOrder.mutateAsync({
        organizationId,
        purchaseOrder: adapted,
      })
    } catch {
      setIsSaving(false)
      enqueueSnackbar(i18n.t("purchaseOrders.saveError"), {
        variant: "error",
      })
      return
    }

    try {
      const data = await convertPurchaseOrderToExpense.mutateAsync({
        organizationId,
        purchaseOrderId,
      })
      setConvertToExpenseDialogOpen(false)
      const expenseIdFromRes =
        data?.expenseId ?? data?.expense_id ?? data?.expense?.id ?? null
      if (expenseIdFromRes != null && String(expenseIdFromRes).trim() !== "") {
        setPurchaseOrderData(prev => ({
          ...prev,
          expenseId: String(expenseIdFromRes),
        }))
      }
    } catch {
      enqueueSnackbar(i18n.t("purchaseOrders.convertToExpenseError"), {
        variant: "error",
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    organizationId,
    purchaseOrderId,
    purchaseOrderData,
    getPurchaseOrderSaveBlocker,
    editPurchaseOrder,
    convertPurchaseOrderToExpense,
    enqueueSnackbar,
    hasLinkedExpense,
  ])

  const resolveCreatedId = data => {
    if (!data || typeof data !== "object") return null
    return data.purchaseOrderId || data.purchase_order_id || data.id || null
  }

  const handleSave = () => {
    if (hasLinkedExpense) {
      if (!organizationId || !purchaseOrderId || purchaseOrderId === "new") {
        return
      }

      setHasValidationErrors(false)
      setIsSaving(true)
      updatePurchaseOrderPaymentStatus.mutate(
        {
          organizationId,
          purchaseOrder: {
            purchaseOrderId,
            paymentStatus: purchaseOrderData.paymentStatus,
          },
        },
        {
          onSuccess: () => {
            setIsSaving(false)
            enqueueSnackbar(i18n.t("purchaseOrders.savedSuccess"), {
              variant: "success",
            })
          },
          onError: () => {
            setIsSaving(false)
            enqueueSnackbar(i18n.t("purchaseOrders.saveError"), {
              variant: "error",
            })
          },
        },
      )
      return
    }

    const blocker = getPurchaseOrderSaveBlocker()
    if (blocker) {
      enqueueSnackbar(i18n.t(blocker.i18nKey), { variant: "error" })
      if (blocker.setHasValidationErrors) setHasValidationErrors(true)
      return
    }

    setHasValidationErrors(false)

    if (!organizationId) {
      console.error("No organizationId, cannot guardar pedido de compra")
      return
    }

    setIsSaving(true)

    const adapted = adaptPurchaseOrderForBackend(purchaseOrderData, {
      isCreate: isNew,
      purchaseOrderId: isNew ? undefined : purchaseOrderId,
      purchaseOrderSerie: isNew ? selectedSerieSK : undefined,
    })

    if (isNew) {
      createPurchaseOrder.mutate(
        { organizationId, purchaseOrder: adapted },
        {
          onSuccess: data => {
            setIsSaving(false)
            if (data?.error || data?.message) {
              enqueueSnackbar(i18n.t("purchaseOrders.saveError"), {
                variant: "error",
              })
              return
            }
            const newId = resolveCreatedId(data)
            enqueueSnackbar(i18n.t("purchaseOrders.createdSuccess"), {
              variant: "success",
            })
            if (newId) {
              navigate(
                projectId
                  ? `/purchase-order/${newId}/${projectId}`
                  : `/purchase-order/${newId}`,
              )
            }
          },
          onError: () => {
            setIsSaving(false)
            enqueueSnackbar(i18n.t("purchaseOrders.saveError"), {
              variant: "error",
            })
          },
        },
      )
      return
    }

    editPurchaseOrder.mutate(
      { organizationId, purchaseOrder: adapted },
      {
        onSuccess: () => {
          setIsSaving(false)
          enqueueSnackbar(i18n.t("purchaseOrders.savedSuccess"), {
            variant: "success",
          })
        },
        onError: () => {
          setIsSaving(false)
          enqueueSnackbar(i18n.t("purchaseOrders.saveError"), {
            variant: "error",
          })
        },
      },
    )
  }

  const headerTitle = isNew
    ? i18n.t("purchaseOrders.editorTitleNew")
    : i18n.t("purchaseOrders.editorTitleEdit")

  const isReady =
    isNew ||
    (!getPurchaseOrder.isLoading &&
      !!getPurchaseOrder.data &&
      !getPurchaseOrder.isError)

  const showFetchError = !isNew && getPurchaseOrder.isError

  const showNoActiveSerieBlock =
    isNew &&
    !purchaseOrderSeries.isLoading &&
    activePurchaseOrderSeries.length === 0

  const purchaseOrderConceptsCount = Array.isArray(
    purchaseOrderData.purchaseOrderConcepts,
  )
    ? purchaseOrderData.purchaseOrderConcepts.length
    : 0
  const convertToExpenseDisabled =
    isNew || purchaseOrderConceptsCount !== 1 || hasLinkedExpense

  if (!user) return null

  return (
    <>
      <Dialog
        open={convertToExpenseDialogOpen}
        onClose={handleCloseConvertToExpenseDialog}
      >
        <DialogTitle>
          {i18n.t("purchaseOrders.convertToExpenseConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("purchaseOrders.convertToExpenseConfirmMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseConvertToExpenseDialog}
            color="secondary"
            disabled={convertPurchaseOrderToExpense.isLoading || isSaving}
          >
            {i18n.t("buttons.cancel")}
          </Button>
          <Button
            onClick={handleConfirmConvertToExpense}
            color="branded"
            variant="contained"
            disabled={convertPurchaseOrderToExpense.isLoading || isSaving}
            sx={{ minWidth: 120 }}
          >
            {convertPurchaseOrderToExpense.isLoading || isSaving ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              i18n.t("purchaseOrders.convertToExpenseConfirm")
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {showFetchError ? (
        <Box sx={{ py: 4 }}>{i18n.t("purchaseOrders.errorLoadingDetail")}</Box>
      ) : isReady ? (
        <Box>
          {showNoActiveSerieBlock && <NoActiveSerieAlert />}
          <Header
            showBack
            title={headerTitle}
            onBack={handleBack}
            onSave={handleSave}
            onConvertToExpense={handleConvertToExpenseClick}
            convertToExpenseDisabled={convertToExpenseDisabled}
            convertToExpenseLoading={
              convertPurchaseOrderToExpense.isLoading ||
              (convertToExpenseDialogOpen && isSaving)
            }
            isSaving={isSaving}
          />
          <PurchaseOrderInfo
            purchaseOrderData={purchaseOrderData}
            setPurchaseOrderData={setPurchaseOrderData}
            hasValidationErrors={hasValidationErrors}
            onInvalidContact={setInvalidContact}
            readOnly={hasLinkedExpense}
            organizationId={organizationId}
            isNew={isNew}
            selectedSerieSK={selectedSerieSK}
            setSelectedSerieSK={setSelectedSerieSK}
          />
          <PurchaseOrderConcepts
            purchaseOrderData={purchaseOrderData}
            setPurchaseOrderData={setPurchaseOrderData}
            hasValidationErrors={hasValidationErrors}
            readOnly={hasLinkedExpense}
          />
          <InvoiceNotes
            invoiceData={purchaseOrderData}
            setInvoiceData={setPurchaseOrderData}
            issued={hasLinkedExpense}
          />
          <InvoicePayment
            invoiceData={purchaseOrderData}
            setInvoiceData={setPurchaseOrderData}
            issued={hasLinkedExpense}
            hasValidationErrors={hasValidationErrors}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  )
}

export default PurchaseOrder
